import React, { useCallback, useContext, useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Box,
  LinearProgress,
  Paper,
  Snackbar,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography
} from '@mui/material';
import { FormattedMessage, useIntl } from 'react-intl';
import axiosConfig from 'src/Resources/axiosConfig';
import {
  OnSiteChecklistItem,
  OnSiteChecklistResponse,
  OnSiteChecklistSignature
} from 'src/interfaces/OnSiteChecklistTypes';
import { UserContext } from 'src/contexts/userContext';
import { UserType } from 'src/interfaces/UserTypes';
import OnSiteChecklistStepRow from './OnSiteChecklistStepRow';
import OnSiteSignatureBlock from './OnSiteSignatureBlock';
import OnSiteVideoLightbox from './OnSiteVideoLightbox';
import InstallationDetailsSection from './InstallationDetailsSection';
import { I_Installation } from 'src/interfaces/InstallationTypes';
import { ProductResource } from 'src/interfaces/ProductResourceTypes';
import useIsMobile from '../../../hooks/useIsMobile';

interface Props {
  installationId: number;
  installation: I_Installation;
}

// Required installation-detail fields that must be filled before signing.
// Mirrors the backend gate in SignOnSiteChecklist.
function isDetailsComplete(i: I_Installation): boolean {
  const required = [
    i.street,
    i.postCode,
    i.city,
    i.canton,
    i.country,
    // Both halves: the ERP write-back joins them into one end_customer_name and skips a fragment.
    i.firstName,
    i.lastName,
    i.email,
    i.networkProvider,
    i.kilowattPeak,
    i.commissionDate,
    i.externalEms,
    i.couplingType,
    i.backupLoad
  ];
  return required.every((v) => !!(v && String(v).trim()));
}

type ToastState = {
  open: boolean;
  severity: 'success' | 'error';
  message: string;
};

function OnSiteChecklistTab({ installationId, installation: installationProp }: Props) {
  const intl = useIntl();
  const isMobile = useIsMobile();
  const { currentUser } = useContext(UserContext);
  const [installation, setInstallation] = useState<I_Installation>(installationProp);
  const [items, setItems] = useState<OnSiteChecklistItem[]>([]);
  const [signature, setSignature] = useState<OnSiteChecklistSignature | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [toast, setToast] = useState<ToastState>({
    open: false,
    severity: 'success',
    message: ''
  });
  const [guidanceResources, setGuidanceResources] = useState<ProductResource[]>([]);
  const [videoResource, setVideoResource] = useState<ProductResource | null>(null);

  const fetchAll = useCallback(() => {
    setLoading(true);
    axiosConfig
      .get('/GetOnSiteChecklistForInstallation', { params: { installationId } })
      .then((res) => {
        const data = res.data as OnSiteChecklistResponse;
        setItems(Array.isArray(data?.items) ? data.items : []);
        setSignature(data?.signature ?? null);
        setError('');
      })
      .catch(() => setError('Failed to load on-site checklist.'))
      .finally(() => setLoading(false));
  }, [installationId]);

  useEffect(() => {
    fetchAll();
  }, [fetchAll]);

  useEffect(() => {
    setInstallation(installationProp);
  }, [installationProp]);

  // Admin-published guidance files for this product (videos pinned to steps, etc.)
  useEffect(() => {
    axiosConfig
      .get('/GetProductResources', { params: { product: installation.product } })
      .then((res) => setGuidanceResources(Array.isArray(res.data) ? res.data : []))
      .catch(() => setGuidanceResources([]));
  }, [installation.product]);

  const isSigned = !!signature?.signedAt;
  const isAdmin = currentUser.userType === UserType.admin;
  const disabled = isSigned && !isAdmin;
  const detailsComplete = useMemo(() => isDetailsComplete(installation), [installation]);

  const progress = useMemo(() => {
    const total = items.length;
    const done = items.filter((i) => i.checked).length;
    const percent = total === 0 ? 0 : Math.round((done / total) * 100);
    return { total, done, percent };
  }, [items]);

  const showSaveError = useCallback(() => {
    setToast({
      open: true,
      severity: 'error',
      message: intl.formatMessage({
        id: 'onSiteSaveFailed',
        defaultMessage: 'Änderung konnte nicht gespeichert werden'
      })
    });
  }, [intl]);

  const handleItemUpdate = useCallback(
    async (
      id: number,
      patch: Partial<{ checked: boolean; comments: string; subtasks: string }>
    ) => {
      const params: Record<string, unknown> = { onSiteChecklistItemId: id };
      if (patch.checked !== undefined) params.checked = patch.checked;
      if (patch.comments !== undefined) params.comments = patch.comments;
      if (patch.subtasks !== undefined) params.subtasks = patch.subtasks;
      try {
        const res = await axiosConfig.put('/UpdateOnSiteChecklistItem', null, { params });
        const updated = res.data as OnSiteChecklistItem;
        setItems((prev) => prev.map((it) => (it.id === id ? updated : it)));
        return true;
      } catch {
        showSaveError();
        return false;
      }
    },
    [showSaveError]
  );

  const handleRemarksChange = useCallback(
    async (remarks: string) => {
      try {
        const res = await axiosConfig.put('/UpdateOnSiteChecklistRemarks', null, {
          params: { installationId, remarks }
        });
        setSignature(res.data as OnSiteChecklistSignature);
        return true;
      } catch {
        showSaveError();
        return false;
      }
    },
    [installationId, showSaveError]
  );

  const handleSign = useCallback(
    async (signedByName: string) => {
      try {
        const res = await axiosConfig.post('/SignOnSiteChecklist', null, {
          params: { installationId, signedByName }
        });
        setSignature(res.data as OnSiteChecklistSignature);
        setToast({
          open: true,
          severity: 'success',
          message: intl.formatMessage({
            id: 'onSiteSignedSuccess',
            defaultMessage: 'Checkliste erfolgreich unterzeichnet'
          })
        });
        return true;
      } catch {
        showSaveError();
        return false;
      }
    },
    [installationId, showSaveError, intl]
  );

  // Group step-pinned guidance files for lookup by a checklist item. Keyed by the stable
  // targetStepKey where the backend has set one, falling back to the positional step number
  // for rows it has not backfilled. Matching on the key means a future insertion or removal
  // of a step cannot silently drag every photo onto the wrong step. General (step null) files
  // are shown in the Documents tab instead, not here.
  const resourcesByStep = useMemo(() => {
    const map: Record<string, ProductResource[]> = {};
    guidanceResources.forEach((r) => {
      const key = r.targetStepKey ?? (r.targetStep != null ? String(r.targetStep) : null);
      if (key !== null) (map[key] ||= []).push(r);
    });
    return map;
  }, [guidanceResources]);

  // An item's own StepKey is authoritative; the number is the fallback for un-backfilled rows.
  const resourcesFor = (item: OnSiteChecklistItem) =>
    resourcesByStep[item.stepKey] ?? resourcesByStep[String(item.stepNumber)] ?? [];

  const handleResourceDownload = (r: ProductResource) => {
    axiosConfig
      .get('/DownloadDocument', { params: { id: r.id }, responseType: 'blob' })
      .then((res) => {
        const url = window.URL.createObjectURL(new Blob([res.data]));
        const link = document.createElement('a');
        link.href = url;
        link.setAttribute('download', r.originalName);
        document.body.appendChild(link);
        link.click();
        link.remove();
        window.URL.revokeObjectURL(url);
      })
      .catch(() => {});
  };

  if (loading) {
    return (
      <Box p={3}>
        <LinearProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Box p={3}>
        <Alert severity="error">{error}</Alert>
      </Box>
    );
  }

  return (
    <Box p={2}>
      <Box mb={2}>
        <Stack
          direction={{ xs: 'column', md: 'row' }}
          justifyContent="space-between"
          alignItems={{ xs: 'flex-start', md: 'center' }}
          spacing={{ xs: 0.5, md: 0 }}
          mb={1}
        >
          <Typography variant="h4">
            <FormattedMessage
              id="onSiteChecklistTitle"
              defaultMessage="Vor-Ort-Installations-Checkliste"
            />
          </Typography>
          <Typography variant="body2" color="text.secondary">
            <FormattedMessage
              id="onSiteChecklistProgress"
              defaultMessage="Fortschritt: {done}/{total} ({percent}%)"
              values={progress}
            />
          </Typography>
        </Stack>
        <LinearProgress
          variant="determinate"
          value={progress.percent}
          sx={{ height: 8, borderRadius: 4 }}
        />
        {disabled && (
          <Alert severity="info" sx={{ mt: 2 }}>
            <FormattedMessage
              id="onSiteLocked"
              defaultMessage="Diese Checkliste wurde unterzeichnet und ist gesperrt."
            />
          </Alert>
        )}
      </Box>

      <InstallationDetailsSection
        installation={installation}
        disabled={disabled}
        isAdmin={isAdmin}
        onSaved={(updated) => setInstallation(updated)}
      />

      {isMobile ? (
        // Phones: one Card per step (see OnSiteChecklistStepRow) so the tab fits
        // the viewport instead of a wide 3-column table that overflows the page.
        <Stack spacing={0}>
          {items.map((item) => (
            <OnSiteChecklistStepRow
              key={item.id}
              item={item}
              disabled={disabled}
              onUpdate={handleItemUpdate}
              resources={resourcesFor(item)}
              onVideoPlay={setVideoResource}
              onResourceDownload={handleResourceDownload}
              isMobile
            />
          ))}
        </Stack>
      ) : (
        <TableContainer component={Paper} variant="outlined">
          <Table>
            <TableHead>
              <TableRow>
                <TableCell sx={{ width: 48 }}>#</TableCell>
                <TableCell>
                  <FormattedMessage id="onSiteStepHeading" defaultMessage="Schritt" />
                </TableCell>
                <TableCell sx={{ width: 260 }}>
                  <FormattedMessage id="onSiteImagesHeading" defaultMessage="Bilder" />
                </TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {items.map((item) => (
                <OnSiteChecklistStepRow
                  key={item.id}
                  item={item}
                  disabled={disabled}
                  onUpdate={handleItemUpdate}
                  resources={resourcesFor(item)}
                  onVideoPlay={setVideoResource}
                  onResourceDownload={handleResourceDownload}
                />
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      {signature && (
        <OnSiteSignatureBlock
          signature={signature}
          disabled={disabled}
          canSign={detailsComplete}
          onRemarksChange={handleRemarksChange}
          onSign={handleSign}
        />
      )}

      <OnSiteVideoLightbox
        resource={videoResource}
        onClose={() => setVideoResource(null)}
      />

      <Snackbar
        open={toast.open}
        autoHideDuration={4000}
        onClose={() => setToast((t) => ({ ...t, open: false }))}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert
          severity={toast.severity}
          onClose={() => setToast((t) => ({ ...t, open: false }))}
        >
          {toast.message}
        </Alert>
      </Snackbar>
    </Box>
  );
}

export default OnSiteChecklistTab;
